import { z } from 'zod';
import { idSchema } from './content-primitives.ts';
import { jsonValueSchema } from './json.ts';
import type { FieldPatch } from './object-patches.ts';
import { atPath } from './translations.ts';

export const jsonPointerSchema = z
  .string()
  .refine(
    (value) => value === '' || (value.startsWith('/') && !/~(?:[^01]|$)/.test(value)),
    'Use an RFC 6901 JSON Pointer.',
  );
const jsonPatchOperationSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('add'), path: jsonPointerSchema, value: jsonValueSchema }),
  z.object({ op: z.literal('replace'), path: jsonPointerSchema, value: jsonValueSchema }),
  z.object({ op: z.literal('test'), path: jsonPointerSchema, value: jsonValueSchema }),
  z.object({ op: z.literal('remove'), path: jsonPointerSchema }),
  z.object({ op: z.literal('move'), path: jsonPointerSchema, from: jsonPointerSchema }),
  z.object({ op: z.literal('copy'), path: jsonPointerSchema, from: jsonPointerSchema }),
]);
export const jsonPatchSchema = z.array(jsonPatchOperationSchema).max(500);
export type JsonPatch = z.infer<typeof jsonPatchSchema>;
export const ifMatchSchema = z
  .string()
  .optional()
  .describe(
    'Optional strong ETag precondition. A mismatch returns 412. Omit to patch the current draft unconditionally.',
  );
export const jsonPatchInputSchema = z.object({ patch: jsonPatchSchema, ifMatch: ifMatchSchema });
export const versionedPatchFields = {
  expectedVersionId: idSchema.describe(
    'Set this to the resource’s current versionId. Any intervening content revision causes a 409.',
  ),
  patch: z
    .array(jsonPatchOperationSchema)
    .max(499)
    .describe('JSON Patch operations applied after the tool tests expectedVersionId.'),
};
export function withVersionTest(expectedVersionId: string, patch: JsonPatch): JsonPatch {
  return [{ op: 'test', path: '/versionId', value: expectedVersionId }, ...patch];
}

/** Object field diffs replace whole arrays; add sets either a new or existing object member. */
export function toJsonPatch(patches: FieldPatch[]): JsonPatch {
  return patches.map((patch) => {
    const path = patch.path.length
      ? '/' + patch.path.map((part) => part.replaceAll('~', '~0').replaceAll('/', '~1')).join('/')
      : '';
    return patch.remove ? { op: 'remove', path } : { op: 'add', path, value: patch.value! };
  });
}

/** Preserve unrelated concurrent edits in browser drafts. JSON Patch cannot test
 * absence, so introducing a missing property falls back to a revision check. */
export function conditionalJsonPatch(before: object, patches: FieldPatch[], versionId: string): JsonPatch {
  const changes = toJsonPatch(patches);
  const previous = patches.map((patch) => atPath(before, patch.path));
  if (previous.some((value) => value === undefined)) return withVersionTest(versionId, changes);
  return [
    ...changes.map((change, i) => ({
      op: 'test' as const,
      path: change.path,
      value: jsonValueSchema.parse(previous[i]),
    })),
    ...changes,
  ];
}
